import { z } from "zod";
import { dateSchema, firestoreTimestampSchema } from "./common.schema";

export const slotDurationRuleKindEnum = z.enum(["permanent", "date_range"]);
export type SlotDurationRuleKind = z.infer<typeof slotDurationRuleKindEnum>;

export const slotDurationMinutesSchema = z.number().int().min(6).max(120);

export const doctorSlotDurationRuleSchema = z.object({
  id: z.string().optional(),
  doctorId: z.string(),
  kind: slotDurationRuleKindEnum,
  durationMinutes: slotDurationMinutesSchema,
  startDate: dateSchema,
  endDate: dateSchema.optional(),
  createdBy: z.string(),
  createdAt: firestoreTimestampSchema.optional(),
  updatedBy: z.string().optional(),
  updatedAt: firestoreTimestampSchema.optional(),
});

export type DoctorSlotDurationRule = z.infer<
  typeof doctorSlotDurationRuleSchema
>;

export const createSlotDurationRuleSchema = z
  .object({
    kind: slotDurationRuleKindEnum,
    durationMinutes: slotDurationMinutesSchema,
    startDate: dateSchema,
    endDate: dateSchema.optional(),
  })
  .superRefine((value, context) => {
    if (value.kind === "date_range" && !value.endDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: "endDate is required for date range rules",
      });
    }
    if (value.endDate && value.endDate < value.startDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: "endDate must be on or after startDate",
      });
    }
    if (value.kind === "permanent" && value.endDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: "endDate is managed by the server for permanent rules",
      });
    }
  });

export type CreateSlotDurationRule = z.infer<
  typeof createSlotDurationRuleSchema
>;

export const updateSlotDurationRuleSchema = createSlotDurationRuleSchema;
export type UpdateSlotDurationRule = z.infer<
  typeof updateSlotDurationRuleSchema
>;

export const querySlotDurationRulesSchema = z.object({
  startDate: dateSchema.optional(),
  endDate: dateSchema.optional(),
});
export type QuerySlotDurationRules = z.infer<
  typeof querySlotDurationRulesSchema
>;

export const slotDurationMutationResultSchema = z.object({
  rule: doctorSlotDurationRuleSchema.nullable(),
  message: z.string(),
  replannedAppointments: z.number().int().nonnegative(),
  overflowAppointments: z.number().int().nonnegative(),
  affectedDates: z.number().int().nonnegative(),
});
export type SlotDurationMutationResult = z.infer<
  typeof slotDurationMutationResultSchema
>;
